/* Copyright (c) 2025 MOCA Systems, Inc.  All rights reserved. */
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { distinctUntilChanged, filter } from 'rxjs/operators';
import { dateCache } from '@touchplan/common';
import { AbstractCanvasConnection } from './AbstractCanvasConnection';
/**
 * A "friendlier" version of the today line that the TodayLineService emits.
 * Converted from a `IFbdbTodayLine` object, which is the raw Firebase data.
 */
export interface ITodayLine {
	date: string;
	x: number;
	server: IFbdbTodayLine;
}
/**
 * Represents the format of today line data as it is from Firebase.
 */
export interface IFbdbTodayLine {
	fbdbTime: string;
	fbdbTimeX: number;
}
/**
 * Configuration settings for date formatting within the service.
 */
const settings = {
	dateFormat: `MMM D YYYY`,
};

@Injectable({ providedIn: 'root' })
export class TodayLineService extends AbstractCanvasConnection<ITodayLine> {
	/**
	 * A cache of the current date
	 * @private
	 */
	private _currentDate: string;
	/**
	 * A cache of the current today line X position
	 * @private
	 */
	private _currentDateX: number;

	/**
	 * Synchronous access to the current formatted date string.
	 * Returns null if no date has been set
	 * Ideally you should be using the changed$ observable for changes.
	 */
	get date(): string | null {
		if (!this._currentDate) {
			return null;
		}

		return this._currentDate;
	}
	/**
	 * Retrieves the current x-coordinate position.
	 * Returns null if no date has been set.
	 */
	get dateX(): number | null {
		// Intentionally checking against the _currentDate and not _currentDateX to avoid 0 value shenanigans
		if (!this._currentDate) {
			return null;
		}

		return this._currentDateX;
	}
	/**
	 * Registers the service to receive today line data from an Observable source.
	 * @param sourceObs$ Observable stream of today line data
	 */
	public registerPlanData(sourceObs$: Observable<IFbdbTodayLine>): void {
		super.registerPlanData();

		this._subscription = sourceObs$
			.pipe(
				filter((data: IFbdbTodayLine) => !!data),
				distinctUntilChanged(
					(prev: IFbdbTodayLine, curr: IFbdbTodayLine) =>
						prev.fbdbTime === curr.fbdbTime && prev.fbdbTimeX === curr.fbdbTimeX
				)
			)
			.subscribe((data: IFbdbTodayLine) => {
				this._currentDateX = data.fbdbTimeX;
				this._currentDate = dateCache.getFormat(data.fbdbTime, settings.dateFormat);
				const todayLine: ITodayLine = {
					date: this._currentDate,
					x: this._currentDateX,
					server: data,
				};
				this._changed$.next(todayLine);
			});
	}
}
